import { useCallback, useMemo } from "react";
import useAuth from "../useAuth";
import usePermission from "../usePermission";
import { useGetCustomersByDealerFirmIdQuery } from "../../store/storageRoles/firmApi";
import { useUpdateFirmDealerRelationMutation } from "../../store";

/**
 * Bayinin müşteri satırları kaynağı (merkez, #376) — useSubFirmActions'ın
 * `customersSource`'u. Müşteri satırında: Kota Ver yalnız bayinin havuzundan,
 * Kota Al canlı okur (#303); Bağı Kopar bayi bağlantısını kaldırır (#202,
 * `firms.editDealerRelation`). Bayi olmayan firmada null döner.
 * (#324: CustomersTab'tan çıkarıldı — Kota & Cihazlar da kullanır.)
 */
export default function useDealerCustomersSource(firm) {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  const isDealerFirm = firm?.firmType === "dealer";
  const canSeeCustomers = hasPermission("firms.customers");

  const { data, isLoading, isFetching, refetch } = useGetCustomersByDealerFirmIdQuery(
    { token: auth?.accessToken, dealerFirmId: firm?._id },
    { skip: !canSeeCustomers || !auth?.accessToken || !firm?._id || !isDealerFirm }
  );
  const [updateDealerRelation, { isLoading: unlinkLoading }] = useUpdateFirmDealerRelationMutation();

  const unlink = useCallback(
    (customer) => {
      const dealerId = String(firm._id);
      const remaining = (customer.dealerFirm || [])
        .map((d) => String(d?._id || d))
        .filter((id) => id !== dealerId);
      return updateDealerRelation({
        id: customer._id,
        dealerFirmIds: remaining,
        token: auth.accessToken,
      }).unwrap();
    },
    [firm?._id, auth?.accessToken, updateDealerRelation]
  );

  return useMemo(
    () =>
      isDealerFirm
        ? {
            customers: canSeeCustomers ? data?.data?.customers || [] : [],
            loading: canSeeCustomers && isLoading,
            fetching: isFetching,
            refetch,
            canSendQuota: hasPermission("firms.sendSubFirmQuota"),
            canUnlink: hasPermission("firms.editDealerRelation"),
            unlink,
            unlinkLoading,
            limitToPool: true,
            requireLiveSource: true,
          }
        : null,
    [isDealerFirm, canSeeCustomers, data, isLoading, isFetching, refetch, hasPermission, unlink, unlinkLoading]
  );
}
